import { collectRoutines, dispatchAction, routine } from 'rxbeach';
import { carry, extractPayload, ofType } from 'rxbeach/operators';
import { forkJoin } from 'rxjs';
import { catchError, flatMap, tap } from 'rxjs/operators';
import { CrudActions, CrudApiService, EntityTypeItem } from './types';

export const crudRoutines = <Request, EntityType extends EntityTypeItem>(
  actions: CrudActions<Request, EntityType>,
  apiService: CrudApiService<Request, EntityType>
) => {
  const genericErrorHandler = catchError((error, stream) => {
    const defaultErrorMessage = 'crud routine error';
    const errorMessage = apiService.handleApiError(error, defaultErrorMessage);
    dispatchAction(actions.error(errorMessage));
    return stream;
  });

  const handleApiGetItems = routine(
    ofType(actions.apiGetItems),
    extractPayload(),
    flatMap((ids) => {
      if (ids.length === 0) return apiService.getAll();
      return forkJoin(ids.map(apiService.getItem));
    }),
    tap((response) => dispatchAction(actions.setItems(response))),
    genericErrorHandler
  );

  const handleApiCreate = routine(
    ofType(actions.apiCreate),
    extractPayload(),
    flatMap((requests) => forkJoin(requests.map(apiService.create))),
    tap((response) => dispatchAction(actions.create(response))),
    genericErrorHandler
  );

  const handleApiDelete = routine(
    ofType(actions.apiDelete),
    extractPayload(),
    carry(flatMap((requests) => forkJoin(requests.map(apiService.remove)))),
    tap(([deletedIds]) => dispatchAction(actions.remove(deletedIds))),
    genericErrorHandler
  );

  const handleApiPut = routine(
    ofType(actions.apiPut),
    extractPayload(),
    flatMap((requests) => forkJoin(requests.map(apiService.put))),
    tap((response) => dispatchAction(actions.put(response))),
    genericErrorHandler
  );

  return collectRoutines(
    handleApiGetItems,
    handleApiCreate,
    handleApiDelete,
    handleApiPut
  );
};
